import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { ConsultationErrorPageComponent } from '@components/consultation-request/error-page/consultation-error-page.component';
import { StdPreventionConsultationRequestCompletedComponent } from '@components/doxy-pep/std-prevention-consultation-request-completed/std-prevention-consultation-request-completed.component';
import { StdConsultationRequestCompletedComponent } from '@components/std-consultation-request-completed/std-consultation-request-completed.component';
import { TreatmentConsultationRequestCompletedComponent } from '@components/treatment-consultation-request-completed/treatment-consultation-request-completed.component';
import { TrtConsultationRequestCompletedComponent } from '@components/trt-consultation-request-completed/trt-consultation-request-completed.component';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ConsultationRequestCompletedGuard implements CanActivate {
  private document = inject<Document>(DOCUMENT);

  /**
   * Determines if the user can activate a particular route based on the current consultation treatment type.
   * Additionally, assigns the corresponding component to the route based on the treatment type.
   *
   * @param {ActivatedRouteSnapshot} route the route to be activated
   *
   * @returns {boolean} whether the route can be activated
   */
  public canActivate(route: ActivatedRouteSnapshot): boolean {
    const treatmentType = route.params.treatmentType?.replace(/-/g, '_');
    const component = this.getComponent(treatmentType);
    if (!component) {
      this.document.location.href = environment.siteUrls.tests;

      return false;
    }

    route.component = component;

    return true;
  }

  /**
   * Retrieves the component corresponding to the given treatment type.
   *
   * @param {string} treatmentType the type of treatment for the consultation
   *
   * @returns {any} the component corresponding to the treatment type or null if no component is found
   */
  private getComponent(treatmentType: string): any {
    switch (treatmentType) {
      case ConsultationTreatmentTypes.Uti:
      case ConsultationTreatmentTypes.YeastInfection:
      case ConsultationTreatmentTypes.BacterialVaginosis:
      case ConsultationTreatmentTypes.Hsv:
      case ConsultationTreatmentTypes.Metformin:
      case ConsultationTreatmentTypes.Glp1:
      case ConsultationTreatmentTypes.Glp1Renewal:
      case ConsultationTreatmentTypes.MaleBv:
      case ConsultationTreatmentTypes.Zofran:
      case ConsultationTreatmentTypes.Hemorrhoids:
      case ConsultationTreatmentTypes.Ed:
      case ConsultationTreatmentTypes.Peptide:
      case ConsultationTreatmentTypes.PeptideRenewal:
      case ConsultationTreatmentTypes.PeptideWs:
      case ConsultationTreatmentTypes.PeptideWsRenewal:
      case ConsultationTreatmentTypes.PeptideGs:
      case ConsultationTreatmentTypes.PeptideGsRenewal:
      case ConsultationTreatmentTypes.PeptideBpc157:
      case ConsultationTreatmentTypes.PeptideBpc157Renewal:
      case ConsultationTreatmentTypes.PeptideGhkCu:
      case ConsultationTreatmentTypes.PeptideGhkCuRenewal:
      case ConsultationTreatmentTypes.PeptideTb500:
      case ConsultationTreatmentTypes.PeptideTb500Renewal:
      case ConsultationTreatmentTypes.PeptideCjc1295:
      case ConsultationTreatmentTypes.PeptideCjc1295Renewal:
      case ConsultationTreatmentTypes.PeptidePt141:
      case ConsultationTreatmentTypes.PeptidePt141Renewal:
      case ConsultationTreatmentTypes.HairLoss:
      case ConsultationTreatmentTypes.Acne:
      case ConsultationTreatmentTypes.Rash:
        return TreatmentConsultationRequestCompletedComponent;
      case ConsultationTreatmentTypes.Trt:
      case ConsultationTreatmentTypes.TrtRenewal:
        return TrtConsultationRequestCompletedComponent;
      case ConsultationTreatmentTypes.StdPrevention:
        if ((environment.site as Sites) === Sites.Starfish) {
          return TreatmentConsultationRequestCompletedComponent;
        }

        return StdPreventionConsultationRequestCompletedComponent;
      case ConsultationTreatmentTypes.Std:
        if ((environment.site as Sites) === Sites.Starfish) {
          return TreatmentConsultationRequestCompletedComponent;
        }

        return StdConsultationRequestCompletedComponent;
      default:
        return ConsultationErrorPageComponent;
    }
  }
}
